import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import Header from '../../components/common/Header';
import Avatar from '../../components/common/Avatar';
import { InlineLoader } from '../../components/common/Loader';
import { getMyAppointmentDetail } from '../../services/patientService';
import { formatDate, formatTime12, formatCurrency, statusLabel } from '../../utils/helpers';

// ── Countdown hook ────────────────────────────────────────────────────────
// Returns { days, hours, minutes, seconds, totalSeconds, expired, started }
function useCountdown(appointmentDate, slotTime) {
  const getTarget = () => {
    if (!appointmentDate || !slotTime) return null;
    try {
      const [h, m] = slotTime.split(':').map(Number);

      // Extract date part in IST (UTC+5:30) to avoid UTC midnight boundary issues on live server
      let datePart;
      if (typeof appointmentDate === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(appointmentDate)) {
        // Already a plain date string — use directly
        datePart = appointmentDate;
      } else {
        // ISO string from DB — convert to IST before extracting date
        const ms    = typeof appointmentDate === 'string'
          ? new Date(appointmentDate).getTime()
          : new Date(appointmentDate).getTime();
        const istMs = ms + 5.5 * 60 * 60 * 1000;
        datePart    = new Date(istMs).toISOString().split('T')[0];
      }

      const [year, month, day] = datePart.split('-').map(Number);
      // Build target in LOCAL time — browser will use device timezone
      const d = new Date(year, month - 1, day, h, m, 0, 0);
      return d;
    } catch { return null; }
  };

  const calc = () => {
    const target = getTarget();
    if (!target) return { days:0, hours:0, minutes:0, seconds:0, totalSeconds:0, expired:true, started:false };
    const diff = Math.floor((target.getTime() - Date.now()) / 1000);
    if (diff <= 0) return { days:0, hours:0, minutes:0, seconds:0, totalSeconds:0, expired:true, started:true };
    const days    = Math.floor(diff / 86400);
    const hours   = Math.floor((diff % 86400) / 3600);
    const minutes = Math.floor((diff % 3600) / 60);
    const seconds = diff % 60;
    return { days, hours, minutes, seconds, totalSeconds: diff, expired: false, started: false };
  };

  const [tick, setTick] = useState(calc);

  useEffect(() => {
    const id = setInterval(() => setTick(calc()), 1000);
    return () => clearInterval(id);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appointmentDate, slotTime]);

  return tick;
}

// ── Countdown display component ───────────────────────────────────────────
function AppointmentCountdown({ appointmentDate, slotTime, status }) {
  const t = useCountdown(appointmentDate, slotTime);

  // Only show for upcoming clinic visits
  if (!['confirmed', 'pending', 'checked-in'].includes(status)) return null;
  if (t.expired) return null;

  const pad = n => String(n).padStart(2, '0');

  // Pick color based on time remaining
  const urgent = t.totalSeconds < 3600;   // < 1 hour
  const soon   = t.totalSeconds < 86400;  // < 24 hours

  const bgClass    = urgent ? 'bg-red-50 border-red-200'     : soon ? 'bg-amber-50 border-amber-200' : 'bg-primary-light border-primary/20';
  const textClass  = urgent ? 'text-red-700'                  : soon ? 'text-amber-700'               : 'text-primary';
  const labelClass = urgent ? 'text-red-500'                  : soon ? 'text-amber-500'               : 'text-primary/70';
  const dotClass   = urgent ? 'bg-red-500 animate-pulse'      : soon ? 'bg-amber-400 animate-pulse'   : 'bg-primary';

  return (
    <div className={`rounded-2xl border p-4 ${bgClass}`}>
      <div className="flex items-center gap-2 mb-3">
        <div className={`w-2 h-2 rounded-full ${dotClass}`} />
        <p className={`text-xs font-bold uppercase tracking-wide ${labelClass}`}>
          {urgent ? 'Appointment Starting Soon!' : soon ? 'Appointment Tomorrow' : 'Time Until Appointment'}
        </p>
      </div>
      {t.days > 0 ? (
        // More than 1 day — show D:H:M
        <div className="flex items-center gap-2">
          {[
            { val: pad(t.days),    lbl: 'Days'    },
            { val: pad(t.hours),   lbl: 'Hours'   },
            { val: pad(t.minutes), lbl: 'Minutes' },
          ].map(({ val, lbl }, i) => (
            <React.Fragment key={lbl}>
              {i > 0 && <span className={`text-xl font-black ${textClass} opacity-40`}>:</span>}
              <div className="flex-1 text-center">
                <div className={`text-3xl font-black font-heading leading-none ${textClass}`}>{val}</div>
                <div className={`text-[10px] font-semibold mt-1 ${labelClass}`}>{lbl}</div>
              </div>
            </React.Fragment>
          ))}
        </div>
      ) : (
        // Less than 1 day — show H:M:S
        <div className="flex items-center gap-2">
          {[
            { val: pad(t.hours),   lbl: 'Hours'   },
            { val: pad(t.minutes), lbl: 'Minutes' },
            { val: pad(t.seconds), lbl: 'Seconds' },
          ].map(({ val, lbl }, i) => (
            <React.Fragment key={lbl}>
              {i > 0 && <span className={`text-xl font-black ${textClass} opacity-40`}>:</span>}
              <div className="flex-1 text-center">
                <div className={`text-3xl font-black font-heading leading-none ${textClass}`}>{val}</div>
                <div className={`text-[10px] font-semibold mt-1 ${labelClass}`}>{lbl}</div>
              </div>
            </React.Fragment>
          ))}
        </div>
      )}
    </div>
  );
}


function InfoRow({ icon, label, value, color = 'text-text-primary' }) {
  if (!value) return null;
  return (
    <div className="flex items-start gap-3 py-2.5 border-b border-border last:border-0">
      <div className="w-8 h-8 rounded-lg bg-surface flex items-center justify-center flex-shrink-0 mt-0.5">
        <i className={`fa-solid ${icon} text-text-muted text-xs`} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs text-text-muted mb-0.5">{label}</p>
        <p className={`text-sm font-semibold ${color} break-all`}>{value}</p>
      </div>
    </div>
  );
}

function Section({ title, icon, children }) {
  return (
    <div className="bg-white rounded-2xl border border-border overflow-hidden mb-3">
      <div className="flex items-center gap-2 px-4 py-3 border-b border-border bg-surface">
        <i className={`fa-solid ${icon} text-primary text-sm`} />
        <h3 className="text-sm font-bold text-text-primary">{title}</h3>
      </div>
      <div className="px-4 py-1">{children}</div>
    </div>
  );
}

export default function AppointmentDetailPage() {
  const { id }   = useParams();
  const navigate = useNavigate();
  const [appt, setAppt]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [qrZoom, setQrZoom]   = useState(false);

  useEffect(() => {
    getMyAppointmentDetail(id)
      .then(res => setAppt(res.appointment))
      .catch(err => { toast.error(err.message || 'Failed to load appointment'); navigate('/appointments'); })
      .finally(() => setLoading(false));
  }, [id, navigate]);

  if (loading) {
    return (
      <div className="page-container">
        <Header title="Appointment Details" showBack />
        <div className="px-4 py-8"><InlineLoader text="Loading details..." /></div>
      </div>
    );
  }

  if (!appt) return null;

  const doc      = appt.doctor || {};
  const payment  = appt.payment || {};
  const qr       = appt.qr     || {};
  const isClinic  = appt.type === 'clinic';
  const isVideo   = appt.type === 'video';
  const isOffline = appt.isOffline === true;

  const statusColors = {
    confirmed:  'text-success',
    'checked-in': 'text-info',
    completed:  'text-primary',
    cancelled:  'text-danger',
    pending:    'text-amber-500',
  };

  return (
    <div className="page-container pb-8">
      <Header title="Appointment Details" showBack />

      <div className="px-4 pt-3 max-w-2xl mx-auto space-y-3">

        {/* ── Status Hero Card ── */}
        <div className={`rounded-2xl p-4 flex items-center gap-4 ${
          appt.status === 'completed'   ? 'bg-green-50 border border-green-200' :
          appt.status === 'cancelled'   ? 'bg-red-50 border border-red-200' :
          appt.status === 'checked-in'  ? 'bg-blue-50 border border-blue-200' :
          'bg-primary-light border border-primary/20'
        }`}>
          <div className={`w-14 h-14 rounded-2xl flex items-center justify-center ${
            appt.status === 'completed'  ? 'bg-green-100' :
            appt.status === 'cancelled'  ? 'bg-red-100' :
            appt.status === 'checked-in'? 'bg-blue-100' :
            'bg-white'
          }`}>
            <i className={`fa-solid ${
              appt.status === 'completed'  ? 'fa-circle-check' :
              appt.status === 'cancelled'  ? 'fa-circle-xmark' :
              appt.status === 'checked-in'? 'fa-person-walking-arrow-right' :
              'fa-calendar-check'
            } text-2xl ${statusColors[appt.status] || 'text-primary'}`} />
          </div>
          <div className="flex-1 min-w-0">
            <p className={`text-lg font-bold font-heading ${statusColors[appt.status] || 'text-primary'}`}>
              {statusLabel(appt.status)}
            </p>
            <p className="text-sm text-text-secondary font-medium">
              {formatDate(appt.appointmentDate)} · {formatTime12(appt.slotTime)}
            </p>
            <p className="text-xs text-text-muted mt-0.5 flex items-center gap-1.5">
              <i className={`fa-solid ${isVideo ? 'fa-video text-info' : 'fa-hospital text-primary'} text-[10px]`} />
              {isVideo ? 'Video Consultation' : 'Clinic Visit'} · Token #{appt.tokenNumber}
            </p>
          </div>
        </div>

        {/* ── Countdown Timer — clinic visits only, upcoming only ── */}
        {isClinic && ['confirmed','pending','checked-in'].includes(appt.status) && (
          <AppointmentCountdown
            appointmentDate={appt.appointmentDate}
            slotTime={appt.slotTime}
            status={appt.status}
          />
        )}

        {/* ── Doctor Info ── */}
        <Section title="Doctor" icon="fa-user-doctor">
          <div className="flex items-center gap-3 py-3">
            <Avatar src={doc.profileImg} name={doc.name} size="md" />
            <div className="flex-1 min-w-0">
              <p className="text-base font-bold text-text-primary">{doc.name || 'Doctor'}</p>
              {doc.speciality && <p className="text-sm text-primary font-medium">{doc.speciality}</p>}
              {doc.clinicName && <p className="text-xs text-text-muted mt-0.5">{doc.clinicName}</p>}
            </div>
            {doc.rating > 0 && (
              <div className="flex items-center gap-1 bg-amber-50 px-2.5 py-1 rounded-lg">
                <i className="fa-solid fa-star text-amber-400 text-xs" />
                <span className="text-xs font-bold text-amber-700">{doc.rating}</span>
              </div>
            )}
          </div>
          {doc.location?.address && (
            <div className="flex items-center gap-2 pb-3 border-t border-border pt-2">
              <i className="fa-solid fa-location-dot text-primary text-xs" />
              <p className="text-xs text-text-secondary flex-1">{doc.location.address}</p>
              {doc.location?.coordinates?.lat && (
                <button
                  onClick={() => window.open(`https://maps.google.com?q=${doc.location.coordinates.lat},${doc.location.coordinates.lng}`, '_blank')}
                  className="text-xs text-primary font-semibold">
                  Directions
                </button>
              )}
            </div>
          )}
        </Section>

        {/* ── Booking ID & Token ── */}
        <Section title="Booking Info" icon="fa-ticket">
          {isClinic && <InfoRow icon="fa-hashtag" label="Booking ID" value={appt.bookingId} color="text-primary" />}
          <InfoRow icon="fa-id-badge"     label="Token Number"  value={`#${appt.tokenNumber}`} />
          <InfoRow icon="fa-calendar"     label="Date"          value={formatDate(appt.appointmentDate)} />
          <InfoRow icon="fa-clock"        label="Time"          value={formatTime12(appt.slotTime)} />
          <InfoRow icon="fa-stethoscope"  label="Type"          value={isVideo ? 'Video Consultation' : isOffline ? 'Walk-in (Offline)' : 'Clinic Visit'} />
        </Section>

        {/* ── Patients ── */}
        {appt.patients?.length > 0 && (
          <Section title="Patient(s)" icon="fa-people-group">
            <div className="py-2 space-y-2">
              {appt.patients.map((p, i) => (
                <div key={i} className="flex items-center gap-3 bg-surface rounded-xl px-3 py-2">
                  <div className="w-8 h-8 rounded-full bg-primary-light flex items-center justify-center">
                    <i className="fa-solid fa-user text-primary text-xs" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-text-primary">{p.name}</p>
                    <p className="text-xs text-text-muted">
                      {p.age ? `${p.age} yrs` : ''}{p.age && p.gender ? ' · ' : ''}{p.gender || ''}
                    </p>
                    {p.address && (
                      <p className="text-xs text-text-muted mt-0.5">
                        <i className="fa-solid fa-location-dot text-primary mr-1 text-[10px]" />{p.address}
                      </p>
                    )}
                    {/* Returning patient discount applied badge */}
                    {p.isOldPatient && payment.discountApplied > 0 && (
                      <div className="mt-1.5 flex items-center gap-1.5 bg-green-50 border border-green-200 rounded-lg px-2 py-1">
                        <i className="fa-solid fa-tag text-green-600 text-[10px]" />
                        <span className="text-[11px] font-semibold text-green-700">
                          Returning patient — ₹{payment.discountApplied} discount applied
                        </span>
                      </div>
                    )}
                    {/* Returning patient — no discount this visit */}
                    {p.isOldPatient && !payment.discountApplied && (
                      <span className="text-[11px] text-text-muted">↩ Returning patient</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
            {/* Discount validity info for returning patients */}
            {appt.patients?.some(p => p.isOldPatient) && appt.doctorDiscount && (
              <div className="mt-2 px-3 py-2.5 bg-primary-light border border-primary/20 rounded-xl flex items-start gap-2">
                <i className="fa-solid fa-clock text-primary text-xs mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-xs font-semibold text-primary">Returning Patient Discount Valid</p>
                  <p className="text-xs text-text-secondary mt-0.5">
                    {appt.doctorDiscount.validityMonths > 0
                      ? `Discount of ${appt.doctorDiscount.type === 'flat' ? `₹${appt.doctorDiscount.value}` : `${appt.doctorDiscount.value}%`} is valid for ${appt.doctorDiscount.validityMonths} month${appt.doctorDiscount.validityMonths !== 1 ? 's' : ''} from your last visit.`
                      : `Lifetime discount of ${appt.doctorDiscount.type === 'flat' ? `₹${appt.doctorDiscount.value}` : `${appt.doctorDiscount.value}%`} — always applies on revisit.`
                    }
                  </p>
                </div>
              </div>
            )}
          </Section>
        )}

        {/* ── QR Code — clinic only, never video ── */}
        {isClinic && !isVideo && qr.image && appt.status !== 'cancelled' && (
          <Section title="Check-in QR Code" icon="fa-qrcode">
            <div className="py-4 flex flex-col items-center gap-3">
              {/* Already checked-in via QR */}
              {qr.usedAt && (
                <div className="w-full space-y-2">
                  <div className="bg-green-50 border border-green-200 rounded-xl px-4 py-3 flex items-center gap-3">
                    <i className="fa-solid fa-circle-check text-success text-xl flex-shrink-0" />
                    <div>
                      <p className="text-sm font-bold text-green-800">QR Already Scanned ✓</p>
                      <p className="text-xs text-green-700">Checked in at clinic via QR code</p>
                      <p className="text-[10px] text-green-600 mt-0.5">
                        {new Date(qr.usedAt).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}
                      </p>
                    </div>
                  </div>
                  <div className="bg-amber-50 border border-amber-200 rounded-xl px-3 py-2 flex items-center gap-2">
                    <i className="fa-solid fa-lock text-amber-500 text-xs flex-shrink-0" />
                    <p className="text-xs text-amber-700">Booking ID check-in is also disabled — this appointment is already verified.</p>
                  </div>
                </div>
              )}

              {/* Already checked-in via Booking ID (no qr.usedAt but status is checked-in/completed) */}
              {!qr.usedAt && ['checked-in', 'completed'].includes(appt.status) && (
                <div className="w-full space-y-2">
                  <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 flex items-center gap-3">
                    <i className="fa-solid fa-id-card text-blue-500 text-xl flex-shrink-0" />
                    <div>
                      <p className="text-sm font-bold text-blue-800">Already Verified via Booking ID ✓</p>
                      <p className="text-xs text-blue-700">Check-in was done using your Booking ID</p>
                    </div>
                  </div>
                  <div className="bg-amber-50 border border-amber-200 rounded-xl px-3 py-2 flex items-center gap-2">
                    <i className="fa-solid fa-lock text-amber-500 text-xs flex-shrink-0" />
                    <p className="text-xs text-amber-700">QR code cannot be scanned again — this appointment is already verified.</p>
                  </div>
                </div>
              )}

              {/* QR ready to scan */}
              {!qr.usedAt && !['checked-in', 'completed'].includes(appt.status) && (
                <>
                  <p className="text-xs text-text-muted text-center">Show this QR at the clinic for check-in</p>
                  <button onClick={() => setQrZoom(true)} className="relative group">
                    <img
                      id="appt-detail-qr-img"
                      src={qr.image}
                      alt="Appointment QR Code"
                      className="w-48 h-48 rounded-xl border-4 border-gray-200 shadow-lg"
                    />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 rounded-xl transition-all flex items-center justify-center">
                      <i className="fa-solid fa-magnifying-glass text-white opacity-0 group-hover:opacity-100 text-xl" />
                    </div>
                  </button>
                  <p className="text-[10px] text-text-muted text-center">Tap QR to zoom</p>
                  {/* Download button */}
                  <button
                    onClick={() => {
                      const img = document.getElementById('appt-detail-qr-img');
                      if (!img) return;
                      const a = document.createElement('a');
                      a.href = img.src;
                      a.download = `FARISTAA-QR-${appt.bookingId || appt._id}.png`;
                      a.click();
                    }}
                    className="flex items-center gap-1.5 mx-auto text-xs text-primary font-bold py-1.5 px-3 rounded-lg bg-primary-light active:scale-95 transition-transform">
                    <i className="fa-solid fa-download text-[11px]" /> Download QR
                  </button>
                  {qr.expiresAt && (
                    <p className="text-xs text-amber-600 text-center">
                      <i className="fa-solid fa-clock mr-1" />
                      Valid until {new Date(qr.expiresAt).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}
                    </p>
                  )}
                </>
              )}
            </div>
          </Section>
        )}

        {/* ── Video Consultation button ── */}
        {isVideo && ['confirmed', 'checked-in'].includes(appt.status) && (
          <Section title="Video Consultation" icon="fa-video">
            <div className="py-3">
              <button
                onClick={() => {
                  const patientId = appt.patientId || appt._id;
                  const doctorId  = doc._id;
                  if (!doctorId) { toast.error('Cannot open chat'); return; }
                  const convId = [patientId.toString(), doctorId.toString()].sort().join('_');
                  navigate(`/chat/${convId}`, { state: { appointmentId: appt._id, doctorName: doc.name, doctorImg: doc.profileImg } });
                }}
                className="btn-primary flex items-center justify-center gap-2">
                <i className="fa-solid fa-video" /> Join Video Consultation
              </button>
            </div>
          </Section>
        )}

        {/* ── Prescription / Notes ── */}
        {(appt.prescription || appt.notes) && (
          <Section title="Doctor's Notes" icon="fa-notes-medical">
            {appt.prescription && (
              <div className="py-2 border-b border-border last:border-0">
                <p className="text-xs text-text-muted mb-1">Prescription</p>
                <p className="text-sm text-text-primary leading-relaxed">{appt.prescription}</p>
              </div>
            )}
            {appt.notes && (
              <div className="py-2">
                <p className="text-xs text-text-muted mb-1">Notes</p>
                <p className="text-sm text-text-secondary leading-relaxed">{appt.notes}</p>
              </div>
            )}
          </Section>
        )}

        {/* ── Payment Breakdown ── */}
        {payment && payment.totalAmount > 0 && (
          <Section title="Payment Details" icon="fa-receipt">
            <div className="py-2 space-y-2.5">
              {payment.doctorFee > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-text-secondary">Consultation Fee</span>
                  <span className="font-medium">{formatCurrency(payment.doctorFee)}</span>
                </div>
              )}
              {payment.discountApplied > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-success flex items-center gap-1">
                    <i className="fa-solid fa-tag text-xs" />
                    {isOffline ? 'Walk-in Discount' : 'Returning Patient Discount'}
                  </span>
                  <span className="font-medium text-success">-{formatCurrency(payment.discountApplied)}</span>
                </div>
              )}
              {payment.convenienceCharge > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-text-secondary">Convenience Charge</span>
                  <span className="font-medium">{formatCurrency(payment.convenienceCharge)}</span>
                </div>
              )}
              {payment.gst > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-text-secondary">GST</span>
                  <span className="font-medium">{formatCurrency(payment.gst)}</span>
                </div>
              )}
              <div className="h-px bg-border" />
              <div className="flex justify-between text-sm font-bold">
                <span className="text-text-primary">Total Paid</span>
                <span className="text-primary">{formatCurrency(payment.totalAmount)}</span>
              </div>
              {payment.refundAmount > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-success flex items-center gap-1">
                    <i className="fa-solid fa-rotate-left text-xs" />Refund
                  </span>
                  <span className="font-bold text-success">+{formatCurrency(payment.refundAmount)}</span>
                </div>
              )}
              <div className="flex items-center gap-2 mt-1">
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                  payment.status === 'released' ? 'bg-green-100 text-green-700' :
                  payment.status === 'refunded' ? 'bg-blue-100 text-info' :
                  payment.status === 'held'     ? 'bg-amber-100 text-amber-700' :
                  'bg-gray-100 text-text-muted'
                }`}>
                  {payment.status === 'released' ? '✓ Payment Released' :
                   payment.status === 'refunded' ? '↩ Refunded' :
                   payment.status === 'held'     ? '⏳ Payment Held' :
                   payment.status}
                </span>
                {payment.paymentMethod && (
                  <span className="text-xs text-text-muted uppercase">{payment.paymentMethod}</span>
                )}
              </div>
            </div>
          </Section>
        )}

      </div>

      {/* ── QR Zoom Modal ── */}
      {qrZoom && qr.image && (
        <>
          <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-6"
            onClick={() => setQrZoom(false)}>
            <div className="bg-white rounded-3xl p-6 flex flex-col items-center gap-4 max-w-xs w-full"
              onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between w-full">
                <p className="font-bold text-text-primary">Scan QR Code</p>
                <button onClick={() => setQrZoom(false)} className="btn-icon">
                  <i className="fa-solid fa-xmark text-text-secondary" />
                </button>
              </div>
              <img src={qr.image} alt="QR Code" className="w-64 h-64 rounded-xl" />
              <p className="text-xs text-text-muted text-center">Show this at the clinic counter</p>
              <p className="text-sm font-bold text-primary">Token #{appt.tokenNumber}</p>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
